
/**
 * src/modules/orders-shared.ts — Sprint 22.B.3 (2026-05-02)
 *
 * Funkcje współdzielone między `orders-list.ts` (per-item rendering w liście)
 * a `orders-detail.ts` (drawer + tile rendering wewnątrz drawera).
 *
 * Wycięte z `orders-detail.ts` żeby moduł `orders-detail` (4128 LOC, ~250 KB minified)
 * mógł być **lazy-loaded** zamiast eager. Te 2 funkcje są wywoływane SYNCHRONICZNIE
 * z `orders-list.ts:1266` (per-item w listing render) — nie da się ich opóźnić.
 *
 * Funkcje:
 *   _buildSbTileHtml(prod, orderQty)     — single tile
 *   _buildSbTilesForItem(it)             — per-item tile(s) — main entry point
 *
 * Globalne helpers (window):
 *   escapeAttr, escapeHtml — z app.js
 *
 * Eksportuje również do `window.__sbExports` + `window.SellBaseApp` dla backward
 * compat z istniejącymi forwarderami w orders-list.ts:27 i innych modułach.
 */

import type { OrderRow, OrderItem, RawOrderItem } from '../types/dto.ts';
import { czesciDruku, szczegolyProdukcji } from './produkcja-opis.ts'; // 06.10.2026: drukarka/koniec/notatka panelu
import { wygasIUsun } from './plynne.ts'; // S-139 (07.10.2026): wygaszenie wiersza/karty zamiast przebudowy listy
import { zglaszanyKomponentami } from './packing-sklad.ts'; // 29.10.2026: produkt „…-SET” zgłaszany tylko komponentami

declare const escapeHtml: (s: unknown) => string;
declare const escapeAttr: (s: unknown) => string;

interface SbProduct {
  id?: number | string;
  name?: string;
  sku?: string;
  /** SKU do zgłoszenia braków: sku produktu z fallbackiem na SKU oferty
   *  z zamówienia (2026-08-27 — produkt bez SKU blokował Telegram/panel). */
  shortage_sku?: string;
  ean?: string;
  /** 2026-09-03: kod produktu SellBase rozni sie od kodu oferty -> czerwono */
  sku_diff?: boolean;
  ean_diff?: boolean;
  location?: string;
  image_url?: string;
  link_quantity?: number;
  comments_count?: number;
  /** 2026-10-01: składniki zestawu i komponenty (taśma, śrubka) — doklejone przez backend */
  zestaw?: any[];
  komponenty?: any[];
}

interface SbOrderItem {
  id?: number | string;
  name?: string;
  quantity?: number;
  sellbase_products?: SbProduct[];
  sellbase_product_id?: number | string;
  sellbase_product_name?: string;
  sellbase_sku?: string;
  sellbase_ean?: string;
  sellbase_location?: string;
  sellbase_image_url?: string;
  sellbase_link_quantity?: number;
  sellbase_comments_count?: number;
  sellbase_zestaw?: any[];
  sellbase_komponenty?: any[];
  zestaw_allegro?: any;
  sku?: string;
  ean?: string;
}

/**
 * Renderuje pojedynczy "kafelek" produktu SellBase (zlinkowanego z item zamówienia).
 *
 * @param prod {id, name, sku, ean, location, image_url, link_quantity}
 * @param orderQty ilość z zamówienia (mnoży się przez link_quantity dla packQty)
 */
// PRODUKCJA v2 (2026-08-19): etykiety twardych znaczników przy produkcie —
// nazwy systemowe, ZERO konfiguracji (decyzja usera: „w montażu = osoba która
// montuje"). Czas: printing liczy z eta („zostało ~X"/„przekroczono"),
// reszta z since (jak długo trwa stan).
var _PROD_ZNACZNIK_META: Record<string, { label: string; color: string; emoji: string }> = {
  queued:     { label: 'w kolejce druku', color: '#94a3b8', emoji: '🖨' },
  printing:   { label: 'w druku',         color: '#3b82f6', emoji: '🖨' },
  printed:    { label: 'wydrukowane',     color: '#22c55e', emoji: '🖨' },
  assembling: { label: 'montuje',         color: '#f59e0b', emoji: '🔧' },
  failed:     { label: 'druk nieudany',   color: '#ef4444', emoji: '⚠' },
  assembled:  { label: 'zmontował',       color: '#22c55e', emoji: '✅' },
  cancelled:  { label: 'anulowano w panelu', color: '#94a3b8', emoji: '✕' },
  // Stan POCZĄTKOWY: zgłoszone, panel jeszcze nie podjął — znacznik pokazuje
  // POWÓD zgłoszenia (label ze słownika firmy), bez czasu i osoby.
  reported:   { label: 'zgłoszone',       color: '#f59e0b', emoji: '⏳' },
};

function _prodCzasZnacznika(z: any): string {
  try {
    if (String(z.status) === 'printing' && z.eta) {
      var ms = new Date(String(z.eta)).getTime() - Date.now();
      if (!isFinite(ms)) return '';
      if (ms <= 0) return 'przekroczono';
      var min = Math.round(ms / 60000);
      return min < 60 ? 'zostało ~' + min + ' min'
                      : 'zostało ~' + Math.floor(min / 60) + ' h ' + (min % 60) + ' min';
    }
    if (z.since) {
      var ms2 = Date.now() - new Date(String(z.since)).getTime();
      if (!isFinite(ms2) || ms2 < 0) return '';
      var min2 = Math.round(ms2 / 60000);
      return min2 < 60 ? min2 + ' min' : Math.floor(min2 / 60) + ' h ' + (min2 % 60) + ' min';
    }
  } catch (_e) { /* ozdobnik */ }
  return '';
}

export function _prodZnacznikHtml(z: any): string {
  if (!z || !z.status) return '';
  var meta = _PROD_ZNACZNIK_META[String(z.status)];
  if (!meta) return '';
  if (String(z.status) === 'reported') {
    // Świeżo zgłoszona pozycja: przy produkcie wisi POWÓD („Zmontować",
    // „Do druku") — decyzja usera; bez czasu, panel nadpisze go statusem.
    var powod = String(z.reason_label || '') || meta.label;
    // escapeHtml TAKŻE na emoji — pole słownika firmy to wolny tekst (strip[:8]),
    // niedomknięty tag potrafiłby połknąć resztę kart listy (innerHTML).
    var ikonka = String(z.reason_emoji || '') || meta.emoji;
    return '<span class="oc-sb-prod-znacznik" style="font-size:11px;font-weight:600;color:' + escapeAttr(meta.color) + ';white-space:nowrap" ' +
      'title="Zgłoszone do produkcji — czeka na podjęcie w panelu">' + escapeHtml(ikonka) + ' ' + escapeHtml(powod) + _iksProdukcji(z) + '</span>';
  }
  var czesci: string[] = [];
  // 06.10.2026 (właściciel): przy druku drukarka i „do HH:MM” z panelu zamiast „kto” (agent farmy); notatka w podpowiedzi
  var druk = czesciDruku(z);
  // „montuje Paweł" / „zmontował Paweł" — osoba zaraz po czasowniku
  if ((String(z.status) === 'assembling' || String(z.status) === 'assembled') && z.worker) {
    czesci.push(meta.label + ' ' + String(z.worker));
  } else {
    czesci.push(meta.label);
    if (druk.drukarka) czesci.push(druk.drukarka);
    else if (z.worker) czesci.push(String(z.worker));
  }
  if (druk.postep) czesci.push(druk.postep);   // 06.10.2026: ile procent druku ukończono
  var czas = druk.koniec || _prodCzasZnacznika(z);
  if (czas && String(z.status) !== 'assembled' && String(z.status) !== 'cancelled') czesci.push(czas);
  var tytul = ['Stan z panelu produkcji p4umake'].concat(szczegolyProdukcji(z)).join('\n');
  return '<span class="oc-sb-prod-znacznik" style="font-size:11px;font-weight:600;color:' + escapeAttr(meta.color) + ';white-space:nowrap" ' +
    'title="' + escapeAttr(tytul) + '">' + meta.emoji + ' ' + escapeHtml(czesci.join(' · ')) + _iksProdukcji(z) + '</span>';
}

/**
 * Krzyżyk zdejmujący znacznik z JEDNEJ pozycji (zgłoszenie usera 2026-08-21).
 * Markup i obsługa kliknięcia żyją w `order-tags.ts` (main chunk) — tu tylko
 * wołamy fabrykę, żeby nie mieć dwóch kopii tego samego przycisku.
 */
function _iksProdukcji(z: any): string {
  if (!z || !z.orderId || !z.sku) return '';
  var ot: any = (window as any).__sbOrderTags;
  if (!ot || !ot.iksZnacznika) return '';
  return ot.iksZnacznika({ id: z.orderId, source: { key: z.source || 'allegro' } }, String(z.sku));
}

// 2026-09-03 (user: „na liście zamówień podświetl SKU/EAN na czerwono, jeżeli różni się
// to z oferty marketplace a produktem SellBase"). Porównanie po znormalizowanym kodzie
// (bez spacji, wielkość liter bez znaczenia), tylko gdy OBA kody są znane. Zestaw
// (kilka produktów SellBase na jednej ofercie) ma inne kody z natury — bez porównania.
export const KOD_DIFF_STYLE = 'color:#dc2626;font-weight:700';
export function _kodNorm(v: any): string { return String(v == null ? '' : v).trim().toUpperCase(); }
export function roznyKod(kodOferty: any, kodProduktu: any): boolean {
  const a = _kodNorm(kodOferty), b = _kodNorm(kodProduktu);
  return !!(a && b && a !== b);
}
export function roznicaKodow(it: any): { sku: boolean; ean: boolean; prodSku: string; prodEan: string } {
  const prods = Array.isArray(it?.sellbase_products) ? it.sellbase_products : [];
  let pSku: any = '', pEan: any = '';
  if (prods.length === 1) { pSku = prods[0]?.sku; pEan = prods[0]?.ean; }
  else if (!prods.length && it?.sellbase_product_id) { pSku = it.sellbase_sku; pEan = it.sellbase_ean; }
  else return { sku: false, ean: false, prodSku: '', prodEan: '' };
  return { sku: roznyKod(it?.sku, pSku), ean: roznyKod(it?.ean, pEan), prodSku: String(pSku || ''), prodEan: String(pEan || '') };
}
export function kodDiffTitle(rodzaj: string, oferta: any, produkt: any): string {
  return rodzaj + ' oferty marketplace (' + String(oferta || '—') + ') różni się od ' + rodzaj + ' produktu SellBase (' + String(produkt || '—') + ')';
}

// 2026-10-02 (user, #118185): „przy pozycji Allegro EAN, SKU, SYGNATURĘ ALLEGRO". Allegro nie ma pola SKU —
// `it.sku` pozycji to sygnatura oferty (offer.external.id), a gdy jej brak, numer oferty
// (core/parsers_impl/line_items.py), więc podpis „SKU" przy nim mylił. SKU = produkt(y) SellBase podpięte
// do oferty; EAN = z pozycji marketplace, a gdy pusty (Allegro nie podaje EAN w zamówieniu) — z produktu.
// Sygnatura na czerwono, gdy różni się od SKU produktu (reguła 2026-09-03; zestaw bez porównania).
// Sygnatura = AKTUALNA z oferty (`offer_sygnatura`, serializer z kopii oferty); zamówienie niesie tę z chwili
// zakupu (`offer_external_id`) — #118185 miało „przejściówka adapter”, 02.10 zmienioną w Allegro na ADP000114-B.
// Ta z zakupu tylko jako `sygnaturaZakupu`, gdy inna (podpowiedź); oferta spoza kopii → sygnatura z zakupu.
export function kodyPozycjiAllegro(it: any): { ean: string; eanDiff: boolean; eanZProduktu: boolean; sku: string; skuLista: string[]; sygnatura: string; sygnaturaZakupu: string; sygnaturaDiff: boolean; prodSku: string; prodEan: string } {
  const prods: any[] = Array.isArray(it?.sellbase_products) && it.sellbase_products.length
    ? it.sellbase_products
    : (it?.sellbase_product_id ? [{ sku: it.sellbase_sku, ean: it.sellbase_ean }] : []);
  const kody = (pole: string): string[] => Array.from(new Set(prods.map((p: any) => String(p?.[pole] || '').trim()).filter(Boolean)));
  const rk = roznicaKodow(it);
  const eanOferty = String(it?.ean || '').trim();
  const eanProd = kody('ean').join(', ');
  const skuLista = kody('sku');
  const zZakupu = String(it?.offer_external_id || '').trim();
  const sygnatura = it?.offer_sygnatura != null ? String(it.offer_sygnatura).trim() : zZakupu;
  return {
    ean: eanOferty || eanProd,
    eanDiff: !!eanOferty && rk.ean,
    eanZProduktu: !eanOferty && !!eanProd,
    sku: skuLista.join(', '),
    skuLista,
    sygnatura,
    sygnaturaZakupu: zZakupu && _kodNorm(zZakupu) !== _kodNorm(sygnatura) ? zZakupu : '',
    sygnaturaDiff: roznyKod(sygnatura, rk.prodSku),
    prodSku: rk.prodSku,
    prodEan: rk.prodEan,
  };
}
export function sygnaturaDiffTitle(sygnatura: any, prodSku: any): string {
  return 'Sygnatura oferty Allegro (' + String(sygnatura || '—') + ') różni się od SKU produktu SellBase (' + String(prodSku || '—') + ')';
}
export function sygnaturaZakupuTitle(sygnaturaZakupu: any): string {
  return sygnaturaZakupu ? 'W chwili zakupu w zamówieniu była sygnatura: ' + String(sygnaturaZakupu) : '';
}

export function _buildSbTileHtml(prod: SbProduct, orderQty: number, znacznik?: any): string {
    // Faza 68: nazwa + obraz produktu klikalne → otwiera drawer produktu (analogicznie do
    // oc-offer-link otwierającego drawer oferty). data-product-id użyte przez event delegation.
    var pidAttr = prod.id ? ' data-product-id="' + escapeAttr(String(prod.id)) + '"' : '';
    var clickableStyle = prod.id ? 'cursor:pointer' : '';
    // 2026-08-17: `sb-zoom` — miniaturka łapana przez hover-zoom (img-preview.ts); sama klasa.
    var imgHtml = prod.image_url
      ? '<img class="oc-sb-img sb-zoom oc-sb-product-link"' + pidAttr + ' src="' + escapeAttr(prod.image_url) + '" alt="" loading="lazy" title="Otwórz produkt" style="' + clickableStyle + '">'
      : '<div class="oc-sb-img-placeholder oc-sb-product-link"' + pidAttr + ' title="Otwórz produkt" style="' + clickableStyle + '"><i data-lucide="package" style="width:16px;height:16px;opacity:.4"></i></div>';
    var metaLine = [];
    if (prod.sku) metaLine.push('SKU: ' + (prod.sku_diff ? '<span class="oc-code-diff" style="' + KOD_DIFF_STYLE + '" title="SKU produktu SellBase różni się od SKU oferty">' + escapeHtml(prod.sku) + '</span>' : escapeHtml(prod.sku)));
    if (prod.ean) metaLine.push('EAN: ' + (prod.ean_diff ? '<span class="oc-code-diff" style="' + KOD_DIFF_STYLE + '" title="EAN produktu SellBase różni się od EAN oferty">' + escapeHtml(prod.ean) + '</span>' : escapeHtml(prod.ean)));
    var packQty = (orderQty || 1) * (prod.link_quantity || 1);
    var _infoHtml =
      imgHtml +
      '<div class="oc-sb-tile-info">' +
        // 2026-07-29: ×N WYPROWADZONE PRZED kafelek (patrz _qtyLeadHtml niżej) — stała
        // kolumna po lewej od zielonego nawiasu; przy kilku produktach ilości układają
        // się w równy słupek i pakowacz widzi je zanim przeczyta nazwy.
        '<div class="oc-sb-tile-name-row">' +
          // 2026-09-01 (zgłoszenie usera): tooltip na nazwie ma pokazywać PEŁNĄ nazwę
          // (ucięte „…" bywa nie do odczytania), nie akcję „Otwórz produkt".
          '<div class="oc-sb-tile-name oc-sb-product-link"' + pidAttr + ' title="' + escapeAttr(prod.name || '') + '" style="' + clickableStyle + '">' + escapeHtml(prod.name || '') + '</div>' +
          // PRODUKCJA v2: twardy znacznik z panelu zaraz PO NAZWIE produktu
          (znacznik ? _prodZnacznikHtml(znacznik) : '') +
        '</div>' +
        (metaLine.length ? '<div class="oc-sb-tile-meta">' + metaLine.join(' · ') + '</div>' : '');
    var _actionsHtml =
      '<div class="oc-sb-tile-actions">' +
        // 2026-08-17 (odbior usera): przycisk braku PRZED lokalizacja; brak
        // lokalizacji = klikalny "Dodaj lokalizacje" (drawer produktu w edycji
        // z fokusem na polu - delegat nizej w tym pliku). Lustro w details.ts.
        '<button class="oc-sb-nostock-btn" onclick="event.stopPropagation()" title="Zgłoś brak / do druku"' +
          ' data-shortage-product-id="' + escapeAttr(String(prod.id || '')) + '"' +
          ' data-shortage-sku="' + escapeAttr(prod.shortage_sku || prod.sku || '') + '"' +
          ' data-shortage-name="' + escapeAttr(prod.name || '') + '"' +
          ' data-shortage-qty="' + packQty + '"' +
          ' data-shortage-img="' + escapeAttr(prod.image_url || '') + '">' +
          '<i data-lucide="alert-circle" style="width:15px;height:15px"></i></button>' +
        lokalizacjaBadgeHtml(prod.id, prod.location) +
      '</div>';
    // 2026-06-05: notatki (gdy comments_count>0) inline w wierszu produktu — po kafelku,
    // na końcu wiersza. Blok async-fill w product-notes.ts.
    var _cc = parseInt(String(prod.comments_count || 0), 10) || 0;
    var _notesHtml = (_cc > 0 && prod.id)
      ? '<div class="oc-sb-tile-notes" data-sb-notes-product="' + escapeAttr(String(prod.id)) + '"></div>'
      : '';
    // 2026-08-17 (decyzja usera): kolejność wiersza = [lokalizacja][brak][×N][kafelek][notatki]
    // — lokalizacja i alert braku PRZED liczbą sztuk, wszystko po lewej.
    // Lustro w orders/details.ts (drawer ma DUPLIKAT renderera — obie kopie idą razem).
    return '<div class="oc-sb-row">' +
      _actionsHtml +
      _qtyLeadHtml(packQty, orderQty, prod.link_quantity) +
      '<div class="oc-sb-tile linked' + (_notesHtml ? ' has-notes' : '') + '"' +
        // PRODUKCJA v2: ramka w kolorze GRUPY roboczej — od razu widać, KTÓRY
        // produkt z zamówienia blokuje realizację.
        (znacznik && znacznik.groupColor
          ? ' style="border:1.5px solid ' + escapeAttr(String(znacznik.groupColor)) + ';border-radius:8px"'
          : '') + '>' +
        _infoHtml + '</div>' + _notesHtml +
      '</div>' +
    '</div>';
}

function _miniaturaSkladu(url: any, ikona: string, pid?: any): string {
  var u = String(url || '');
  var pidAttr = pid ? ' data-product-id="' + escapeAttr(String(pid)) + '"' : '';
  var klasa = pid ? ' oc-sb-product-link' : '';
  return u
    ? '<img class="sb-thumb sb-zoom' + klasa + '"' + pidAttr + ' src="' + escapeAttr(u) + '" alt="" loading="lazy" style="width:22px;height:22px;border-radius:4px;object-fit:cover;flex-shrink:0' + (pid ? ';cursor:pointer' : '') + '">'
    : '<span class="' + klasa.trim() + '"' + pidAttr + ' style="width:22px;height:22px;border-radius:4px;background:var(--color-surface-2);display:inline-flex;align-items:center;justify-content:center;flex-shrink:0' + (pid ? ';cursor:pointer' : '') + '"><i data-lucide="' + ikona + '" style="width:12px;height:12px;opacity:.5"></i></span>';
}

// 2026-10-01 (właściciel, #118815: „nie można zgłosić braku komponentu, tylko cały produkt”) — mały wykrzyknik przy
// składniku zestawu i komponencie. Ta sama klasa co przy produkcie: delegat (shortage-report.ts) zbiera wszystkie
// przyciski karty do jednego okna zgłoszenia. `pid` komponentu = karta, z której powstał (pusty = wpisany ręcznie).
function _brakSkladuHtml(pid: any, sku: any, nazwa: any, ile: number, img: any, notka: string,
                         zestaw?: { id?: any; name?: any }): string {
  return '<button type="button" class="oc-sb-nostock-btn" onclick="event.stopPropagation()" title="Zgłoś brak / do druku"' +
    // 27.10.2026: składnik wie, z którego zestawu pochodzi — okno zgłoszenia pomija wtedy sam zestaw
    (zestaw ? ' data-shortage-zestaw-id="' + escapeAttr(zestaw.id ? String(zestaw.id) : '') + '"' +
              ' data-shortage-zestaw-name="' + escapeAttr(String(zestaw.name || '')) + '"' : '') +
    ' data-shortage-product-id="' + escapeAttr(pid ? String(pid) : '') + '"' +
    ' data-shortage-sku="' + escapeAttr(String(sku || '')) + '"' +
    ' data-shortage-name="' + escapeAttr(String(nazwa || '')) + '"' +
    ' data-shortage-qty="' + ile + '"' +
    ' data-shortage-img="' + escapeAttr(String(img || '')) + '"' +
    ' data-shortage-note="' + escapeAttr(notka) + '"' +
    ' style="height:22px;min-width:26px;padding:0 4px;border-radius:6px">' +
    '<i data-lucide="alert-circle" style="width:13px;height:13px"></i></button>';
}

function _wierszKomponentu(k: any, ile: number, dla?: string, rodzic?: { id?: any; name?: any }): string {
  return '<div class="oc-komp-row" style="display:flex;align-items:center;gap:6px;font-size:12px;min-width:0">' +
    _brakSkladuHtml(k && k.source_product_id, k && k.sku, k && k.name, ile, k && k.image_url, 'Komponent' + (dla ? ' do: ' + dla : ''), rodzic) +
    '<span class="oc-sb-qty-lead" style="min-width:30px" title="Do dołożenia: ' + ile + ' szt.">×' + ile + '</span>' +
    _miniaturaSkladu(k && k.image_url, 'paperclip') +
    '<span style="font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="' + escapeAttr(String((k && k.name) || '')) + '">' + escapeHtml(String((k && k.name) || '')) + '</span>' +
    (k && k.description ? '<span style="color:var(--color-text-muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="' + escapeAttr(String(k.description)) + '">' + escapeHtml(String(k.description)) + '</span>' : '') +
    (k && k.sku ? '<span style="color:var(--color-text-muted);white-space:nowrap">SKU: ' + escapeHtml(String(k.sku)) + '</span>' : '') +
    (k && k.location ? '<span style="color:var(--color-text-muted);white-space:nowrap">📍 ' + escapeHtml(String(k.location)) + '</span>' : '') +
  '</div>';
}

/**
 * 2026-10-01 (właściciel): pod kafelkiem produktu — zestaw rozbity na składniki (SKU, EAN, lokalizacja, ilość)
 * i komponenty (taśma, śrubka) do dołożenia. `packQty` = sztuki kafelka (ilość × krotność powiązania).
 * Lustro w orders/details.ts woła ten sam helper (eksport), więc lista i karta zamówienia się nie rozjeżdżają.
 */
export function _skladIKomponentyHtml(prod: { id?: any; name?: string; sku?: any; zestaw?: any[]; komponenty?: any[] } | null | undefined, packQty: number): string {
  var zestaw = prod && Array.isArray(prod.zestaw) ? prod.zestaw : [];
  var komp = prod && Array.isArray(prod.komponenty) ? prod.komponenty : [];
  if (!zestaw.length && !komp.length) return '';
  var q = Math.max(1, packQty || 1);
  var nazwa = String((prod && prod.name) || '');
  var naglowek = function(ikona: string, tekst: string): string {
    return '<div style="font-size:10.5px;font-weight:700;letter-spacing:.3px;text-transform:uppercase;color:var(--color-text-muted);display:flex;align-items:center;gap:5px;margin-top:2px">' +
      '<i data-lucide="' + ikona + '" style="width:12px;height:12px"></i>' + tekst + '</div>';
  };
  // contain: inline-size — długie nazwy składników nie rozpychają karty ani listy zamówień (2026-10-01)
  var html = '<div class="oc-sklad" style="margin:2px 0 8px 16px;padding:4px 0 4px 10px;border-left:2px solid var(--color-border);display:flex;flex-direction:column;gap:4px;min-width:0;contain:inline-size">';
  if (zestaw.length) {
    html += naglowek('boxes', 'Zestaw — spakuj składniki');
    zestaw.forEach(function(c: any) {
      var ile = q * (parseInt(String(c && c.per_unit), 10) || 1);
      var meta = [];
      if (c && c.sku) meta.push('SKU: ' + escapeHtml(String(c.sku)));
      if (c && c.ean) meta.push('EAN: ' + escapeHtml(String(c.ean)));
      var lok = lokalizacjaBadgeHtml(c && c.id, c && c.location);
      var pidAttr = c && c.id ? ' data-product-id="' + escapeAttr(String(c.id)) + '"' : '';
      html += '<div class="oc-sklad-row" style="display:flex;align-items:center;gap:6px;font-size:12px;min-width:0">' +
        _brakSkladuHtml(c && c.id, c && c.sku, c && c.name, ile, c && c.image_url, 'Składnik zestawu' + (nazwa ? ': ' + nazwa : ''),
                        { id: prod && prod.id, name: nazwa }) +
        '<span class="oc-sb-qty-lead" style="min-width:30px" title="Do spakowania: ' + ile + ' szt.">×' + ile + '</span>' +
        _miniaturaSkladu(c && c.image_url, 'package', c && c.id) +
        '<span class="oc-sb-product-link"' + pidAttr + ' style="font-weight:600;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="' + escapeAttr(String((c && c.name) || '')) + '">' + escapeHtml(String((c && c.name) || '')) + '</span>' +
        (meta.length ? '<span style="color:var(--color-text-muted);white-space:nowrap">' + meta.join(' · ') + '</span>' : '') +
        lok +
      '</div>';
      (Array.isArray(c && c.komponenty) ? c.komponenty : []).forEach(function(k: any) {
        html += '<div style="margin-left:36px">' + _wierszKomponentu(k, ile * (parseInt(String(k && k.per_unit), 10) || 1), String((c && c.name) || '')) + '</div>';
      });
    });
  }
  if (komp.length) {
    html += naglowek('paperclip', 'Włóż do paczki');  // S-121: „dołóż” brzmiało jak dodatkowo ponad to, co wyżej
    // 29.10.2026: komponenty produktu „…-SET” niosą jego id i nazwę — okno zgłoszenia pomija wtedy sam produkt
    var rodzic = zglaszanyKomponentami(prod && prod.sku) ? { id: prod && prod.id, name: nazwa } : undefined;
    komp.forEach(function(k: any) { html += _wierszKomponentu(k, q * (parseInt(String(k && k.per_unit), 10) || 1), nazwa, rodzic); });
  }
  return html + '</div>';
}

/**
 * ×N przed kafelkiem produktu (2026-07-29, prośba usera).
 *
 * Stała kolumna po LEWEJ od zielonego nawiasu — ilości z kilku pozycji tworzą równy
 * słupek. `packQty` = ilość z zamówienia × mnożnik powiązania: gdy mnożnik ≠ 1 (jedna
 * oferta = kilka sztuk magazynowych), tooltip tłumaczy skąd liczba, bo inaczej „×2"
 * przy „×1" na Allegro wygląda jak błąd.
 */
function _kopiujAwaryjnie(tekst: string): boolean {
  try {
    var pole = document.createElement('textarea');
    pole.value = tekst;
    pole.setAttribute('readonly', 'readonly');
    pole.style.cssText = 'position:fixed;top:-1000px;opacity:0';
    document.body.appendChild(pole);
    pole.select();
    var ok = document.execCommand('copy');
    document.body.removeChild(pole);
    return !!ok;
  } catch (_e) {
    return false;
  }
}

function _qtyLeadHtml(packQty: number, orderQty?: number, linkQty?: number): string {
  var lq = linkQty || 1;
  var oq = orderQty || 1;
  var title = lq !== 1
    ? 'Do spakowania: ' + packQty + ' szt. (' + oq + ' × ' + lq + ' szt. w powiązaniu)'
    : 'Do spakowania: ' + packQty + ' szt.';
  return '<span class="oc-sb-qty-lead oc-qty-pulse" title="' + escapeAttr(title) + '">×' + packQty + '</span>';
}

/**
 * Buduje HTML dla wszystkich kafelków produktów SellBase związanych z item zamówienia.
 * Item może mieć: multiple linked products (sellbase_products[]), single legacy linked
 * product (sellbase_product_id), lub być unlinked (placeholder + "Przypisz produkt" btn).
 */
export function _buildSbTilesForItem(it: SbOrderItem, znaczniki?: any): string {
    // PRODUKCJA v2: `znaczniki` = mapa SKU → {status, worker, eta, since,
    // groupColor} z order.production — znacznik i ramka na ZGŁOSZONYM produkcie.
    var _zn = function(sku: any): any {
      if (!znaczniki || !sku) return null;
      return znaczniki[String(sku)] || null;
    };
    // If item has multiple linked products, render tile for each
    var orderQty = it.quantity ?? 1;
    if (it.sellbase_products && it.sellbase_products.length > 0) {
      return it.sellbase_products.map(function(p) {
        return _buildSbTileHtml({
          id: p.id, name: p.name, sku: p.sku, ean: p.ean,
          shortage_sku: p.sku || it.sku,
          location: p.location, image_url: p.image_url, link_quantity: p.link_quantity,
          comments_count: p.comments_count
        }, orderQty, _zn(p.sku)) + _skladIKomponentyHtml(p, (orderQty || 1) * (p.link_quantity || 1));
      }).join('');
    }
    // Single linked product (backward compat)
    if (it.sellbase_product_id) {
      var _rk = roznicaKodow(it);
      return _buildSbTileHtml({
        id: it.sellbase_product_id, name: it.sellbase_product_name || it.name,
        sku: it.sellbase_sku || it.sku, ean: it.sellbase_ean || it.ean,
        sku_diff: _rk.sku, ean_diff: _rk.ean,
        location: it.sellbase_location, image_url: it.sellbase_image_url,
        link_quantity: it.sellbase_link_quantity,
        comments_count: it.sellbase_comments_count
      }, orderQty, _zn(it.sellbase_sku || it.sku)) +
        _skladIKomponentyHtml({ id: it.sellbase_product_id, name: it.sellbase_product_name || it.name, sku: it.sellbase_sku || it.sku, zestaw: it.sellbase_zestaw, komponenty: it.sellbase_komponenty },
                              (orderQty || 1) * (it.sellbase_link_quantity || 1));
    }
    // Unlinked
    var assignBtn = '<button class="btn-primary btn-xs assign-product-btn" style="font-size:10px" data-item-id="' + escapeAttr(String(it.id || '')) + '" onclick="event.stopPropagation()">Przypisz produkt</button>';
    // 2026-07-29: ×N także przed kafelkiem niepowiązanym — słupek ilości musi być
    // ciągły, inaczej wiersz bez produktu SellBase wypada z rytmu skanowania wzrokiem.
    return '<div class="oc-sb-row">' +
      _qtyLeadHtml(orderQty) +
      '<div class="oc-sb-tile unlinked">' +
        '<div class="oc-sb-img-placeholder warn"><i data-lucide="alert-triangle" style="width:14px;height:14px;color:#f59e0b"></i></div>' +
        '<div class="oc-sb-tile-info">' +
          '<div class="oc-sb-tile-name" style="color:var(--color-text-muted)">' + escapeHtml(it.name) + '</div>' +
          '<div class="oc-sb-tile-meta" style="color:#f59e0b">Brak powiązanego produktu SellBase</div>' +
        '</div>' +
        '<div style="display:flex;align-items:center;gap:4px">' + assignBtn + '</div>' +
      '</div>' +
    '</div>';
}

// ── normalizeBackendOrder (Sesja 4 Krok 1 — migrated from app.js) ──
// Maps raw backend order JSON do ujednoliconego formatu używanego w UI
// (drawer, list, detail). Wymaga window globals: sourceMetaFromRaw,
// normalizeStatusLabel, statusColorForLabel, normalizePaymentStatusFromBackend,
// normalizePaymentMethodLabel, parseDeliveryAddress.
export function normalizeBackendOrder(o: any): OrderRow | null {
  const w = window as any;
  if (!o) return null;
  const itemsRaw = Array.isArray(o.items) ? o.items : [];
  const items = itemsRaw.map(function(i: RawOrderItem) {
    let qty = parseInt(String(i.quantity || 1), 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    let unit = parseFloat(String(i.unit_price != null ? i.unit_price : i.price));
    if (isNaN(unit)) unit = 0;
    let total = parseFloat(String(i.total));
    if (isNaN(total)) total = unit * qty;
    let variant = '';
    if (typeof i.variant === 'string') variant = i.variant.trim();
    const _vattrs = i.variant_attributes;
    if (!variant && _vattrs && typeof _vattrs === 'object') {
      const parts: string[] = [];
      Object.keys(_vattrs).forEach(function(k) {
        const vv = _vattrs[k];
        if (!k || vv == null) return;
        const kv = String(k).trim();
        const vvTxt = String(vv).trim();
        if (!kv || !vvTxt) return;
        parts.push(kv + ': ' + vvTxt);
      });
      if (parts.length) variant = parts.join(' | ');
    }
    return {
      id: i.id || i.line_item_id || '',
      line_item_id: i.line_item_id || i.id || '',
      sku: i.sku || i.offer_external_id || i.offer_id || '',
      ean: i.ean || '',
      variant: variant,
      offer_id: i.offer_id || '',
      offer_external_id: i.offer_external_id || '',
      // 2026-10-02: aktualna sygnatura z oferty (brak pola = oferty nie ma w kopii → zostaje ta z zakupu)
      offer_sygnatura: i.offer_sygnatura != null ? String(i.offer_sygnatura) : undefined,
      name: i.name || 'Produkt',
      quantity: qty,
      unit_price: unit,
      total: total,
      sellbase_product_id: i.sellbase_product_id || null,
      sellbase_product_name: i.sellbase_product_name || '',
      sellbase_sku: i.sellbase_sku || '',
      sellbase_ean: i.sellbase_ean || '',
      sellbase_location: i.sellbase_location || '',
      sellbase_image_url: i.sellbase_image_url || '',
      sellbase_price_net: i.sellbase_price_net || null,
      sellbase_link_quantity: parseInt(String(i.sellbase_link_quantity)) || 1,
      sellbase_comments_count: i.sellbase_comments_count || 0,
      sellbase_products: Array.isArray(i.sellbase_products) ? i.sellbase_products : null,
      // 2026-10-01: skład zestawu i komponenty (taśma, śrubka) pojedynczego powiązania — karta, lista, pakowanie
      sellbase_zestaw: Array.isArray(i.sellbase_zestaw) ? i.sellbase_zestaw : null,
      sellbase_komponenty: Array.isArray(i.sellbase_komponenty) ? i.sellbase_komponenty : null,
      // S-57 (02.10): „Wielopak/Zestaw Allegro” przed tytułem pozycji (lista, szczegół, pakowanie) — normalizacja go gubiła
      zestaw_allegro: i.zestaw_allegro || null,
      // 2026-09-03: stawka VAT, netto i obraz pozycji — karta zamówienia ręcznego pokazuje
      // cenę jednostkową, VAT i wartość przy pozycji (wcześniej normalizacja je gubiła).
      vat_rate: (i.vat_rate != null && i.vat_rate !== '') ? i.vat_rate : (i.vat_rate_value != null ? i.vat_rate_value : null),
      price_net: i.price_net != null ? i.price_net : null,
      total_net: i.total_net != null ? i.total_net : null,
      image_url: i.image_url || '',
      offer_image_url: i.offer_image_url || ''
    };
  });
  const itemsTotal = items.reduce(function(sum: number, i: OrderItem) { return sum + (i.total || 0); }, 0);
  let deliveryCost = parseFloat(o.delivery_cost);
  if (isNaN(deliveryCost)) deliveryCost = 0;
  let amount = parseFloat(o.payment_amount);
  if (isNaN(amount) || amount <= 0) {
    const derived = itemsTotal + (deliveryCost > 0 ? deliveryCost : 0);
    amount = derived > 0 ? derived : itemsTotal;
  }
  let paidAmount = parseFloat(o.payment_paid_amount);
  if (isNaN(paidAmount) || paidAmount < 0) paidAmount = 0;
  let refundedAmount = parseFloat(o.payment_refunded_amount);
  if (isNaN(refundedAmount) || refundedAmount < 0) refundedAmount = 0;
  let dueAmount = parseFloat(o.payment_due_amount);
  if (isNaN(dueAmount) || dueAmount < 0) {
    dueAmount = amount > paidAmount ? (amount - paidAmount) : 0;
  }
  const paymentCurrency = String(o.payment_currency || 'PLN').trim().toUpperCase() || 'PLN';
  const source = w.sourceMetaFromRaw(o.source);
  const statusLabel = w.normalizeStatusLabel(o.status);
  // ETAP 3 (2026-08-10): `key` to KLUCZ statusu, nie slug jego nazwy.
  //
  // Najpierw `status_key` WPROST Z API (serializer liczy go z kolumny, więc zna też
  // nazwy nadane przez firmę). Dopiero gdy go nie ma — stary serwer, zamówienie
  // złożone lokalnie — schodzimy do rozpoznawania po napisie.
  //
  // Wcześniej klucz powstawał przez `label.toLowerCase()`, czyli był SLUGIEM NAZWY:
  // po przemianowaniu „Spakowane" na „Zapakowane" ten sam status dostawał klucz
  // `zapakowane` i przestawał pasować do filtra, licznika i logiki pakowania.
  const statusKey = String(o.status_key || '')
    || ((w.__sbExports && typeof w.__sbExports.kluczStatusu === 'function')
      ? String(w.__sbExports.kluczStatusu(o.status) || '')
      : statusLabel.toLowerCase().replace(/\s+/g, '_'));
  const statusObj = { key: statusKey, label: statusLabel, color: w.statusColorForLabel(statusLabel) };
  // 2026-08-14 (życzenie usera): marketplace mówi „pobranie" → status płatności
  // POKAZUJE Pobranie. Serwer oddaje efektywny wymiar (payment_dim, POBRANIE
  // WYGRYWA — jedno źródło prawdy z grupami/paskiem); surowy payment_status przy
  // COD wisi 'pending' na zawsze (Allegro nie stempluje opłaty pobrania).
  const _dim = String(o.payment_dim || '').toLowerCase();
  const paymentStatus = (_dim === 'cod' || _dim === 'cod_paid')
    ? _dim
    : w.normalizePaymentStatusFromBackend(o.payment_status, amount, paidAmount, refundedAmount);
  const shipmentStatus = String(o.shipment_status || 'no_shipment').toLowerCase();

  const addr = w.parseDeliveryAddress(o.delivery_address || {});
  // 2026-06-10: telefon z ADRESU DOSTAWY — bywa INNY niz buyer_phone (konto/paragon,
  // np. zagraniczny). Nadania WzA/paczkomat musza preferowac numer adresowy;
  // wczesniej normalizacja go gubila i modal prefillowal telefon kupujacego.
  let _rawDa: any = o.delivery_address;
  if (typeof _rawDa === 'string') { try { _rawDa = JSON.parse(_rawDa); } catch (e) { _rawDa = null; } }
  const deliveryPhone = String((_rawDa && (_rawDa.phoneNumber || _rawDa.phone_number || _rawDa.phone)) || '').trim();
  const deliveryRecipientVerified = o.delivery_recipient_verified !== false;
  const deliveryRecipientPending = !!o.delivery_recipient_pending;
  const deliveryRecipientSource = String(o.delivery_recipient_source || 'marketplace');
  const firstName = String(o.buyer_first_name || '').trim();
  const lastName = String(o.buyer_last_name || '').trim();
  const buyerTypeRaw = String(o.buyer_type || (o.buyer_company_name ? 'company' : 'individual'));
  const customerName = (buyerTypeRaw === 'company' && o.buyer_company_name)
    ? String(o.buyer_company_name).trim()
    : (firstName + ' ' + lastName).trim() || o.buyer_login || 'Klient';
  const boughtDate = o.bought_at ? String(o.bought_at) : '';
  const history = Array.isArray(o.status_history) ? o.status_history : [];
  const comments = Array.isArray(o.comments) ? o.comments : [];

  return {
    id: 'AL-' + String(o.id || '').toUpperCase(),
    allegro_id: o.id,
    public_id: o.public_id || null,  // 2026-06-01: ULID SellBase (klucz techniczny)
    short_no: (o.short_no != null && String(o.short_no).match(/^\d+$/)) ? parseInt(o.short_no, 10) : (parseInt(o.order_number || 0, 10) || null),  // 2026-09-04: numer SellBase
    orderNumber: parseInt(o.order_number || 0, 10) || null,
    customer: customerName,
    customerEmail: o.buyer_email || '',
    customerPhone: o.buyer_phone || '',
    email: o.buyer_email || '',
    phone: o.buyer_phone || '',
    deliveryPhone: deliveryPhone,
    deliveryRecipientVerified: deliveryRecipientVerified,
    deliveryRecipientPending: deliveryRecipientPending,
    deliveryRecipientSource: deliveryRecipientSource,
    // raw passthrough — _wzaResolveOrderAddress czyta z niego phoneNumber adresu dostawy
    delivery_address: o.delivery_address || null,
    customerId: o.customer_id || ((o.buyer_login || customerName || 'KL').toString().slice(0, 12).toUpperCase()),
    status: statusObj,
    source: source,
    source_order_id: o.id,
    amount: amount,
    currency: paymentCurrency,
    marketplace_id: o.marketplace_id || '',
    shipment_status: shipmentStatus,
    productCount: items.reduce(function(s: number, it: OrderItem) { return s + (it.quantity || 0); }, 0) || items.length || 0,
    date: boughtDate,
    dateFormatted: boughtDate ? new Date(boughtDate).toLocaleDateString('pl-PL') : '',
    waybills: (function() { try { const wb = o.waybills_json; return typeof wb === 'string' ? JSON.parse(wb) : (Array.isArray(wb) ? wb : []); } catch(e) { return []; } })(),
    // S-244: numery nadania z rozliczeń Allegro (tylko GET szczegółów; lista ich nie niesie)
    numery_nadania_allegro: Array.isArray(o.numery_nadania_allegro) ? o.numery_nadania_allegro : [],
    allegroFulfillmentStatus: String(o.marketplace_fulfillment_status || o.fulfillment_status || ''),
    allegroRevision: String(o.allegro_revision || o.checkout_form_revision || ''),
    lastSyncSource: String(o.last_sync_source || ''),
    lastSyncedFulfillmentStatus: String(o.last_synced_fulfillment_status || ''),
    lastAllegroEventId: String(o.last_allegro_event_id || o.last_event_id || ''),
    lastSyncError: String(o.last_sync_error || ''),
    shipmentSummaryLineItemsSent: !!o.shipment_summary_line_items_sent,
    payment: {
      status: paymentStatus,
      method: w.normalizePaymentMethodLabel(o.payment_method) || o.delivery_method || 'Płatność',
      amount: amount,
      currency: paymentCurrency,
      paid_amount: paidAmount,
      refunded_amount: refundedAmount,
      due_amount: dueAmount,
      operator: o.payment_operator || '',
      type: o.payment_type || '',
      payment_id: o.payment_id || '',
      raw_status: o.payment_raw_status || '',
      paid_at: o.payment_paid_at || ''
    },
    items: items,
    products: items.map(function(i: OrderItem) { return i.name || "Produkt"; }),
    shippingAddress: addr,
    billingAddress: w.parseDeliveryAddress(o.billing_address || o.delivery_address || {}),
    delivery: {
      method: o.delivery_method || '',
      address: o.delivery_address || {},
      tracking: o.tracking_number || null,
      pickup_point: o.delivery_pickup || null,
      recipient_verified: deliveryRecipientVerified,
      recipient_pending: deliveryRecipientPending,
      recipient_source: deliveryRecipientSource
    },
    locker_id: o.delivery_pickup && o.delivery_pickup.id ? o.delivery_pickup.id : '',
    comments: comments,
    statusHistory: history,
    history: history,
    events: Array.isArray(o.events) ? o.events : [],
    buyerGuest: !!o.buyer_guest,
    buyerAllegroId: String(o.buyer_allegro_id || ''),
    buyerType: buyerTypeRaw,
    buyerCompanyName: String(o.buyer_company_name || ''),
    buyerNip: String(o.invoice_tax_id || ''),
    buyerLanguage: String(o.buyer_language || ''),
    invoiceRequired: !!o.invoice_required,
    invoiceUserOverridden: !!o.invoice_user_overridden,
    invoiceCompanyName: String(o.invoice_company_name || ''),
    invoiceTaxId: String(o.invoice_tax_id || ''),
    invoicePersonFirstName: String(o.invoice_person_first_name || ''),
    invoicePersonLastName: String(o.invoice_person_last_name || ''),
    invoiceStreet: String(o.invoice_street || ''),
    invoiceCity: String(o.invoice_city || ''),
    invoiceZip: String(o.invoice_zip || ''),
    invoiceCountryCode: String(o.invoice_country_code || ''),
    deliveryCostAmount: parseFloat(o.delivery_cost) || 0,
    // 2026-09-22: dopłaty kupującego (Allegro surcharges) — karta PŁATNOŚĆ.
    surcharges: Array.isArray(o.surcharges) ? o.surcharges : [],
    surchargesTotal: parseFloat(o.surcharges_total) || 0,
    // 2026-09-25: historia płatności (płatność główna + dopłaty) — sekcja w szufladzie zamówienia.
    paymentHistory: Array.isArray(o.payment_history) ? o.payment_history : [],
    buyer_login: String(o.buyer_login || ''),
    buyer_address: String(o.buyer_address || ''),
    buyer_city: String(o.buyer_city || ''),
    buyer_postal_code: String(o.buyer_postal_code || ''),
    buyer_country: String(o.buyer_country || 'PL'),
    // 2026-07-29: wiadomość kupującego ma 4 różne nośniki per marketplace — backend
    // sprowadza je do `buyer_message`, ale zostawiamy stare klucze dla payloadów,
    // które omijają listing (drawer, TikTok `buyer_message`, VH `comment`).
    message_to_seller: String(o.message_to_seller || o.buyer_message || o.comment || o.buyerCheckoutNotes || ''),
    allegroAccountName: String(o.allegro_account_name || ''),
    // FIX 2026-07-11: allegroAccountId TYLKO dla zamówień Allegro. account_id NIE jest globalnie
    // unikalny — dla źródeł nie-Allegro (PS/eBay/Erli/VH) cudze account_id wciekało tu i drawer
    // pokazywał login konta Allegro o kolidującym id (+ prefetch złego konta w WzA).
    allegroAccountId: source.key === 'allegro' ? (parseInt(o.allegro_account_id || o.account_id || 0) || 0) : 0,
    is_allegro: source.key === 'allegro',
    notes: String(o.notes || ''),
    // 2026-07-29: notatka wewnętrzna (komentarz zamówienia) z backendu — pasek na karcie.
    internal_note: (o.internal_note && o.internal_note.text) ? o.internal_note : null,
    // ETAP 4, POPRAWKA (2026-08-10): backend oddaje TABLICĘ OBIEKTÓW {id,name,color},
    // a nie napis JSON. Poprzednia wersja wołała `JSON.parse` na tablicy — JS zamieniał
    // ją wtedy na „[object Object]", parse rzucał i chipy CICHO znikały. Tag był
    // w bazie, był w odpowiedzi API i nie pojawiał się na karcie.
    // Napis zostaje obsłużony dla zgodności ze starym kształtem pola.
    tags: (function() {
      if (Array.isArray(o.tags)) return o.tags;
      try { return JSON.parse(o.tags || '[]'); } catch (e) { return []; }
    })(),
    // PRODUKCJA v2: stan produkcji z enricha listingu (nagłówek + pozycje +
    // grupa) — bez przepisania tutaj cała warstwa znaczników była martwa.
    production: o.production || null,
    // 2026-08-21: grupa ROBOCZA dla kazdego wiersza listy (listing dokłada ją
    // niezaleznie od `production`, ktore powstaje tylko dla zamowien ze zgloszeniami
    // produkcji). Bez tego chip grupy na karcie klamalby o stanie.
    grupa_reczna: o.grupa_reczna || null,
    // 2026-10-08 (#119452): otwarte zlecenia „Przypisz pracownika” — plakietka osoby na karcie listy
    zlecenia: Array.isArray(o.zlecenia) ? o.zlecenia : [],
    // 2026-09-02: ZNACZNIK SYSTEMOWY czarnej listy z enricha listingu — normalizacja
    // przepisuje białą listę pól, bez tej linii chip „CZARNA LISTA" na karcie był martwy
    // mimo `blacklisted: true` w odpowiedzi API (wykryte w przeglądarce na sandboksie).
    blacklisted: !!o.blacklisted,
    // 2026-08-28: przy wyszukiwaniu globalnym backend dokłada grupę EFEKTYWNĄ
    // (ręczna ma pierwszeństwo, potem pierwsza pasująca automatyczna). Pole musi
    // przeżyć normalizację, żeby każdy renderer mógł wyjaśnić, gdzie znaleziono
    // zamówienie, mimo że aktywna grupa jest na czas wyszukiwania pomijana.
    effective_group: o.effective_group || o.grupa_efektywna || null,
    group_context: o.group_context || null
  } as any;
}

/**
 * Pasek „wiadomość klienta / notatka wewnętrzna" (2026-07-29, prośba usera).
 *
 * Pomarańczowy, bo to informacja, którą trzeba PRZECZYTAĆ zanim paczka pójdzie
 * (np. „proszę dopisać na fakturze OST-1030"). Rozdzielone ikoną i etykietą —
 * pakowacz musi wiedzieć, czy to słowa klienta, czy nasza wewnętrzna decyzja.
 * Treść przycięta do 2 linii (rekord na prodzie ma 918 znaków — bez clampu karta
 * puchnie), pełna w tooltipie, klik rozwija.
 */
/** Czy klucz statusu zamowienia pasuje do filtra paska?
 *
 *  Filtr bywa ZLEPKIEM kluczy po przecinku — klik w grupe wysyla cala galaz
 *  (`new,processing,ready_to_ship,...`). Porownanie ROWNOSCIA gubilo wtedy
 *  wszystkie pobrane zamowienia: serwer zwracal komplet, a client-side
 *  „bezpiecznik" wycinal 100% i grupa 2+ statusow pokazywala „Brak zamowien"
 *  przy niezerowym liczniku (bug z proda, zgloszenie pracownika 2026-08-13).
 *  Grupy 1-statusowe przechodzily, stad bug dlugo uchodzil za dzialanie. */
/** #2 serii (2026-08-15): JEDEN wspólny sposób odświeżenia listy zamówień po
 *  zapisie z modala (braki, przypisania itd.). Bez czyszczenia cache stron
 *  (sessionStorage TTL 60 s) reload odda STARE dane — dokładnie ten rozjazd,
 *  przez który po zgłoszeniu braku tag pojawiał się dopiero po F5. */
export function odswiezListeZamowien(): void {
  try {
    const doUsuniecia: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && (k.indexOf('__sb_orders_pg:') === 0 || k.indexOf('__sb_orders_search:') === 0)) {
        doUsuniecia.push(k);
      }
    }
    doUsuniecia.forEach(function (k) { sessionStorage.removeItem(k); });
  } catch (_) { /* cache best-effort */ }
  const w = window as any;
  if (typeof w.loadAllegroOrdersIntoPanel === 'function') w.loadAllegroOrdersIntoPanel(true);
  else if (typeof w.renderOrdersView === 'function') w.renderOrdersView();
}

/**
 * Formatuje numer telefonu DO WYŚWIETLENIA (2026-08-16).
 *
 * Numer kupującego przychodzi z Allegro ze spacjami („+48 792 672 580"),
 * a numer z adresu dostawy zbity („+48792672580") — bez wspólnego formatera
 * drawer pokazywał dwa różne zapisy tego samego numeru.
 *
 * TYLKO numery polskie (+48 / 48 / gołe 9 cyfr → grupy 3-3-3): formatów
 * zagranicznych NIE zgadujemy — inne prefiksy krajów i inne długości wracają
 * bez zmian, bo źle pogrupowany numer wygląda gorzej niż zbity.
 *
 * DISPLAY-ONLY: wyłącznie do escapeHtml(...) wyświetlanego tekstu. Do zapisu,
 * inputów edycji i renderSellBaseCallActions(...) idzie SUROWA wartość —
 * backend/mostek telefoniczny dostają numer w takiej postaci, w jakiej
 * przyszedł z marketplace'u.
 */
export function formatujTelefon(surowy: any): string {
  if (surowy == null) return '';
  var wejscie = String(surowy);
  if (!wejscie.trim()) return '';
  // Do analizy zdejmujemy tylko spacje/myślniki/nawiasy.
  var zbity = wejscie.replace(/[\s\-()]/g, '');
  var cyfry = '';
  if (/^\+48\d{9}$/.test(zbity)) cyfry = zbity.slice(3);
  else if (/^48\d{9}$/.test(zbity)) cyfry = zbity.slice(2);
  else if (/^\d{9}$/.test(zbity)) {
    return zbity.slice(0, 3) + ' ' + zbity.slice(3, 6) + ' ' + zbity.slice(6);
  }
  if (cyfry) {
    return '+48 ' + cyfry.slice(0, 3) + ' ' + cyfry.slice(3, 6) + ' ' + cyfry.slice(6);
  }
  // Każdy inny kształt — wejście bez zmian.
  return wejscie;
}

export function statusFilterMatches(key: string, filter: string): boolean {
  var f = String(filter == null ? '' : filter).toLowerCase();
  if (!f || f === 'all') return true;
  var k = String(key == null ? '' : key).toLowerCase();
  if (f.indexOf(',') < 0) return k === f;
  var czesci = f.split(',');
  for (var i = 0; i < czesci.length; i++) {
    if (czesci[i].trim() === k) return true;
  }
  return false;
}

/**
 * S-139 (audyt 07.10): czy KLUCZ statusu zostaje widoczny pod aktywnym filtrem paska —
 * ten sam warunek, jaki cards.ts/table.ts/virtual.ts stosują per-wiersz przy renderze listy.
 * Trzymany w jednym miejscu, żeby punktowa podmiana po zapisie (bez pełnego renderOrdersView)
 * decydowała identycznie jak sam filtr.
 */
export function zamowienieWidoczneWFiltrze(key: string): boolean {
  var st: any = (window as any).__sbState || {};
  if (isOrdersGlobalSearchActive()) return true;
  if (Array.isArray(st.ordersFilterStatuses) && st.ordersFilterStatuses.length) return true;
  var f = st.ordersStatusFilter;
  if (!f || f === 'all') return true;
  return statusFilterMatches(String(key || ''), f);
}

/**
 * S-139: zamówienie, które po zapisie wypada z widocznej listy (status spoza filtra, zdjęty
 * tag/grupa z filtra itd.) — wygaszenie karty/wiersza w miejscu (plynne.ts: `wygasIUsun`),
 * bez przebudowy całej listy. Dotyka wszystkich trybów renderu (karta/tabela/wirtualizacja) —
 * w DOM istnieje naraz tylko jeden z nich.
 */
export function wygasZamowienieZListy(orderId: string | number): void {
  try {
    var w: any = window;
    var esc = (w.CSS && w.CSS.escape) ? w.CSS.escape(String(orderId)) : String(orderId).replace(/["\\]/g, '\\$&');
    var wszystkie = document.querySelectorAll('[data-order-id="' + esc + '"]');
    wszystkie.forEach(function (el: Element) {
      // `data-order-id` siedzi też na checkboxie i na pigułce statusu wewnątrz karty/wiersza —
      // wygaszamy wyłącznie sam kontener wiersza/karty.
      if (el.classList && (el.classList.contains('order-card') || el.classList.contains('order-row'))) {
        // S-139 przegląd: kontener (grid/tbody/lista) złapany PRZED wygaszeniem — po `remove()`
        // element sam nie ma już rodzica, a sprzątanie osieroconego nagłówka/pustej strony
        // dotyczy właśnie tego, co w nim zostało.
        var kontener = el.parentElement;
        wygasIUsun(el, { poUsunieciu: function () { _posprzatajListeZamowienPoWygaszeniu(kontener); } });
      }
    });
  } catch (_e) { /* best-effort — brak wiersza = user i tak nie widzi listy */ }
}

/**
 * S-139 przegląd: po wygaszeniu ostatniej karty w grupie dat (widok kart, domyślny sort)
 * nagłówek „Dzisiaj/Wczoraj/…” zostawał osierocony bez żadnej karty pod nim; po wygaszeniu
 * OSTATNIEJ karty na całej stronie kontener był po prostu pusty — bez komunikatu „Brak
 * zamówień” i bez dociągnięcia kolejnych pozycji. Nagłówek sprzątamy lokalnie (kosmetyka —
 * nic nie trzeba sprawdzać na serwerze); pustą stronę doganiamy cichym refetchem całej listy
 * (`odswiezListeZamowien` niżej: cache + `loadAllegroOrdersIntoPanel(true)`) — przy wątpliwości,
 * czy jest kolejna strona/pozycja do dociągnięcia, poprawność danych ma pierwszeństwo przed
 * zgadywaniem lokalnie.
 */
function _posprzatajListeZamowienPoWygaszeniu(kontener: Element | null): void {
  if (!kontener) return;
  try {
    kontener.querySelectorAll('.orders-date-group').forEach(function (naglowek: Element) {
      var kolejny = naglowek.nextElementSibling;
      if (!kolejny || kolejny.classList.contains('orders-date-group')) naglowek.remove();
    });
  } catch (_e) { /* kosmetyka — brak sprzątania nagłówka nie psuje danych */ }
  try {
    if (!kontener.querySelector('.order-card, .order-row')) odswiezListeZamowien();
  } catch (_e) { /* refetch dogoni przy najbliższym tiku */ }
}

/** Wyszukiwanie serwerowe uruchamia się od dwóch znaków i obejmuje wszystkie
 * grupy. Stan wybranej grupy zostaje nietknięty, dzięki czemu po wyczyszczeniu
 * pola użytkownik automatycznie wraca do poprzedniego widoku. */
// 2026-09-02 (user: „w każdym zamówieniu ma być możliwość edycji"): nadpisanie SellBase
// (sellbase_edit_json: pozycje/ceny/wysyłka) ma pierwszeństwo przed snapshotem marketplace.
// Tabela Allegro dostaje je już z serializera backendu; źródła mapowane na froncie
// (PS/eBay/Erli/TikTok/VH) — tu, po mapperze. Ten sam helper stosuje odpowiedź
// endpointu /lines na obiekt zamówienia po zapisie.
// 2026-09-25: implementacja w ./order-edit-apply.ts (bez importów — używają jej też mappery master/*).
export { sellbaseEditItemsToOrderItems, applySellbaseEditToOrder, applySellbaseEditFromRaw } from './order-edit-apply.ts';

export function isOrdersSearchActive(): boolean {
  if (typeof window === 'undefined') return false;
  var st: any = (window as any).__sbState || {};
  var q = String(st.ordersFilterQuery || '').trim();
  if (!q && typeof document !== 'undefined') {
    var input = document.getElementById('order-search') as HTMLInputElement | null;
    q = String((input && input.value) || '').trim();
  }
  return q.length >= 2;
}

/** 2026-09-02 (user: „dlaczego pomija grupę, w której jestem?"): wyszukiwanie szuka
 * DOMYŚLNIE w wybranej grupie/statusie. „Wszystkie grupy" = świadomy wybór (przycisk
 * w notce) albo automatyczne przejście, gdy w grupie nie ma ani jednego trafienia.
 * Tylko w tym trybie lista pomija filtr grupy i pokazuje kontekst grupy przy wierszu. */
export function isOrdersGlobalSearchActive(): boolean {
  if (!isOrdersSearchActive()) return false;
  var st: any = (window as any).__sbState || {};
  return !!st.ordersSearchAllGroups;
}

function _ordersSearchScopeToggle(allGroups: boolean): void {
  var st: any = (window as any).__sbState || {};
  st.ordersSearchAllGroups = allGroups;
  st.ordersSearchScopeAuto = false;
  // 2026-09-03 (audyt): w obie strony pamiętamy frazę — „szukaj tylko w tej grupie" z 0 trafień
  // nie może od razu wracać do wszystkich grup (auto-fallback tylko raz na frazę).
  st.__ordersSearchFallbackQuery = String(st.ordersFilterQuery || '').trim();
  st.currentPage = st.currentPage || {};
  st.currentPage.orders = 1;
  syncOrdersSearchScopeUI();
  var w: any = window as any;
  var loader = (w.__sbExports && w.__sbExports.loadAllegroOrdersIntoPanel) || w.loadAllegroOrdersIntoPanel;
  if (typeof loader === 'function') { try { loader(true); } catch (_e) {} }
}

function _ordersSelectedScopeLabel(): string {
  if (typeof document === 'undefined') return '';
  var active = document.querySelector('#orders-status-panel .osp-item.active') as HTMLElement | null;
  if (!active) return '';
  var named = active.querySelector('.osp-grupa-nazwa') as HTMLElement | null;
  if (named && named.textContent) return named.textContent.trim();
  var first = active.querySelector('span') as HTMLElement | null;
  return String((first && first.textContent) || '').replace(/\s+/g, ' ').trim();
}

/** Synchronizuje informację o zakresie wyszukiwania i przygasza zapamiętaną
 * grupę wyłącznie wizualnie. Nie zmienia filtrów ani sessionStorage. */
export function syncOrdersSearchScopeUI(): void {
  if (typeof document === 'undefined') return;
  var st: any = (window as any).__sbState || {};
  var searching = isOrdersSearchActive();
  if (!searching) { st.ordersSearchAllGroups = false; st.ordersSearchScopeAuto = false; }
  var allGroups = searching && !!st.ordersSearchAllGroups;
  var note = document.getElementById('orders-search-scope-note');
  var panel = document.getElementById('orders-status-panel');
  if (panel) panel.classList.toggle('is-search-suspended', allGroups);
  if (!note) return;
  note.hidden = !searching;
  note.classList.toggle('is-active', searching);
  note.classList.toggle('is-all-groups', allGroups);
  if (!searching) return;
  var scope = _ordersSelectedScopeLabel();
  var scopeLc = scope.toLowerCase();
  var hasScope = !!scope && scopeLc !== 'wszystkie' && scopeLc.indexOf('wszystkie zam') !== 0;
  var body = note.querySelector('[data-orders-search-scope-body]') as HTMLElement | null;
  if (!body) return;
  var esc = function(t: string) { return String(t || '').replace(/[&<>"]/g, function(c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[c]; }); };
  var btn = function(all: boolean, label: string) {
    return '<button type="button" class="orders-search-scope-toggle" data-orders-search-scope-toggle="' + (all ? 'all' : 'scoped') + '">' + esc(label) + '</button>';
  };
  var html = '';
  if (allGroups) {
    html += '<strong>' + (st.ordersSearchScopeAuto && hasScope
      ? 'Brak wyników w grupie „' + esc(scope) + '" — pokazano wyniki ze wszystkich grup'
      : 'Wyniki ze wszystkich grup') + '</strong>';
    html += '<small>' + (hasScope ? 'Wybrana grupa (' + esc(scope) + ') jest na czas wyszukiwania pomijana. ' + btn(false, 'Szukaj tylko w tej grupie') + ' ' : '')
      + (hasScope ? 'Po wyczyszczeniu wyszukiwania wrócisz do: ' + esc(scope) + '.' : 'Po wyczyszczeniu wyszukiwania wrócisz do poprzedniego widoku.') + '</small>';
  } else {
    html += '<strong>' + (hasScope ? 'Szukasz w grupie: ' + esc(scope) : 'Szukasz we wszystkich zamówieniach') + '</strong>';
    html += '<small>' + (hasScope ? 'Wyniki tylko z tej grupy. ' + btn(true, 'Szukaj we wszystkich grupach') : 'Wybierz grupę po lewej, aby zawęzić wyniki.') + '</small>';
  }
  body.innerHTML = html;
  if (!(note as any)._scopeBound) {
    (note as any)._scopeBound = true;
    note.addEventListener('click', function(e: any) {
      var t = e.target && (e.target as HTMLElement).closest('[data-orders-search-scope-toggle]') as HTMLElement | null;
      if (!t) return;
      e.preventDefault();
      _ordersSearchScopeToggle(t.getAttribute('data-orders-search-scope-toggle') === 'all');
    });
  }
}

/** Mała etykieta nad wynikiem globalnego wyszukiwania. Backend jest źródłem
 * prawdy dla grup automatycznych; `grupa_reczna` zostaje fallbackiem dla danych
 * z cache lub starszego serwera. */
export function orderSearchGroupContextHtml(order: any, inline?: boolean): string {
  if (!isOrdersGlobalSearchActive()) return '';
  var extraClass = inline ? ' orders-search-result-group--inline' : '';
  var group = order && (order.effective_group || order.grupa_efektywna || order.grupa_reczna);
  var context = order && order.group_context;
  if (!group || !String(group.name || group.nazwa || '').trim()) {
    // „Poza grupami" wolno pokazać tylko po udanym rozstrzygnięciu. Brak pola
    // lub awaria backendu nie może być przedstawiona jako pewny stan biznesowy.
    if (!context || context.resolved !== true) {
      return '<div class="orders-search-result-group orders-search-result-group--unresolved' + extraClass + '">' +
        '<span class="orders-search-result-group-dot"></span><span>Nie udało się ustalić grupy</span></div>';
    }
    return '<div class="orders-search-result-group orders-search-result-group--none' + extraClass + '">' +
      '<span class="orders-search-result-group-dot"></span><span>Poza grupami</span></div>';
  }
  var name = String(group.name || group.nazwa || '').trim();
  var type = String(group.type || group.typ || group.group_type || '').toLowerCase();
  var manualFallback = !!(order && order.grupa_reczna && group === order.grupa_reczna);
  var manual = manualFallback || type === 'manual' || type === 'reczna' || type === 'ręczna' || !!group.manual;
  var color = String(group.color || group.kolor || '#64748b').trim();
  if (!/^#[0-9a-f]{3,8}$/i.test(color)) color = '#64748b';
  return '<div class="orders-search-result-group' + (manual ? ' orders-search-result-group--manual' : '') + extraClass +
    '" style="--orders-result-group-color:' + escapeAttr(color) + '">' +
    '<span class="orders-search-result-group-dot"></span><span>' +
    (manual ? 'Grupa robocza: ' : 'Grupa: ') + '<strong>' + escapeHtml(name) + '</strong></span></div>';
}

export function buildOrderNoteBarHtml(order: any): string {
  var buyerMsg = String((order && (order.message_to_seller || order.buyer_message)) || '').trim();
  var note = (order && order.internal_note) || null;
  var noteText = note ? String(note.text || '').trim() : '';
  if (!buyerMsg && !noteText) return '';
  var rows = '';
  if (buyerMsg) {
    rows += '<div class="oc-note-row" data-note-kind="buyer" title="' + escapeAttr(buyerMsg) + '">' +
      '<i data-lucide="message-square" class="oc-note-ico"></i>' +
      '<span class="oc-note-label">Klient:</span>' +
      '<span class="oc-note-text">' + escapeHtml(buyerMsg) + '</span>' +
    '</div>';
  }
  if (noteText) {
    var author = String(note.author || '').trim();
    var extra = (note.count && note.count > 1) ? (' +' + (note.count - 1)) : '';
    rows += '<div class="oc-note-row" data-note-kind="internal" title="' + escapeAttr(noteText) + '">' +
      '<i data-lucide="sticky-note" class="oc-note-ico"></i>' +
      '<span class="oc-note-label">Notatka:</span>' +
      '<span class="oc-note-text">' + escapeHtml(noteText) + '</span>' +
      (author ? '<span class="oc-note-author">' + escapeHtml(author) + escapeHtml(extra) + '</span>' : '') +
    '</div>';
  }
  return '<div class="oc-note-bar">' + rows + '</div>';
}

// ── Backward compat: expose do window dla forwarderów w innych modułach ─
// (orders-list.ts:27, orders-detail.ts:1155-1215, etc. — wszystkie obecnie wołają
// `window.__sbExports._buildSbTilesForItem` lub `window._buildSbTilesForItem`)
if (typeof window !== 'undefined') {
  const w: any = window;
  w._buildSbTileHtml = _buildSbTileHtml;
  w._buildSbTilesForItem = _buildSbTilesForItem;
  w.SellBaseApp = w.SellBaseApp || {};
  w.SellBaseApp._buildSbTileHtml = _buildSbTileHtml;
  w.SellBaseApp._buildSbTilesForItem = _buildSbTilesForItem;
  w.__sbExports = w.__sbExports || {};
  w.__sbExports._buildSbTileHtml = _buildSbTileHtml;
  w.__sbExports._buildSbTilesForItem = _buildSbTilesForItem;
  // Sesja 4 Krok 1 — migrated z app.js
  w.normalizeBackendOrder = normalizeBackendOrder;
  w.SellBaseApp.normalizeBackendOrder = normalizeBackendOrder;
  w.__sbExports.normalizeBackendOrder = normalizeBackendOrder;
  // 2026-07-29: pasek notatki/wiadomości — wołany też z packing.ts (inny chunk).
  w.buildOrderNoteBarHtml = buildOrderNoteBarHtml;
  w.SellBaseApp.buildOrderNoteBarHtml = buildOrderNoteBarHtml;
  w.__sbExports.buildOrderNoteBarHtml = buildOrderNoteBarHtml;
  // #7 (2026-08-13): wspolny matcher filtra statusu — uzywany przez cards/table/virtual.
  w.__sbExports.statusFilterMatches = statusFilterMatches;
  // S-139 (07.10.2026): płynne zapisy — jeden matcher filtra + wygaszenie wiersza/karty,
  // używane przez order-drawer-core.ts, orders/drawer.ts, cards.ts, order-tags.ts, order-groups-quick.ts.
  w.__sbExports.zamowienieWidoczneWFiltrze = zamowienieWidoczneWFiltrze;
  w.__sbExports.wygasZamowienieZListy = wygasZamowienieZListy;
  w.__sbExports.isOrdersGlobalSearchActive = isOrdersGlobalSearchActive;
  w.__sbExports.syncOrdersSearchScopeUI = syncOrdersSearchScopeUI;
  w.__sbExports.orderSearchGroupContextHtml = orderSearchGroupContextHtml;
  w.__sbExports.odswiezListeZamowien = odswiezListeZamowien;
  // 2026-08-16: wspólny formater telefonu (display-only, tylko PL) — patrz komentarz przy funkcji.
  w.__sbExports.formatujTelefon = formatujTelefon;

  // 2026-08-25 (user): „po kliknięciu w SKU nie otwiera drawera tylko kopiuje je
  // do schowka". Delegat na document, bo karty listy przerysowują się przy każdym
  // odświeżeniu — listener podpięty do elementu nie przeżyłby pierwszego renderu.
  // Faza capture + stopPropagation: klik NIE może dojść do karty zamówienia.
  if (!w.__sbSkuCopyBound) {
    w.__sbSkuCopyBound = true;
    document.addEventListener('click', function(e: any) {
      var el = e.target && e.target.closest && e.target.closest('.oc-sku-copy[data-sku]');
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      var sku = String(el.getAttribute('data-sku') || '').trim();
      if (!sku) return;
      var nazwa = String(el.getAttribute('data-kod-nazwa') || 'SKU'); // 2026-10-02: sygnatura Allegro też się kopiuje
      var potwierdz = function(ok: boolean) {
        try { w.showToast?.(ok ? 'Skopiowano ' + nazwa + ': ' + sku : 'Nie udało się skopiować do schowka', ok ? 'success' : 'error'); }
        catch (_e) { /* toast jest dodatkiem, nie warunkiem */ }
      };
      // clipboard API bywa niedostępne poza HTTPS/po odmowie uprawnień — wtedy
      // stara metoda przez ukryte pole.
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(sku).then(function() { potwierdz(true); })
          .catch(function() { potwierdz(_kopiujAwaryjnie(sku)); });
      } else {
        potwierdz(_kopiujAwaryjnie(sku));
      }
    }, true);
  }

  // 21.10.2026 (właściciel: „na liście zamówień, karcie zamówienia też zrób edytowalną lokalizację”): klik w znaczek
  // lokalizacji (także „Dodaj lokalizację”) = pole do wpisania W MIEJSCU — bez szuflady produktu. JEDEN delegat na
  // document (kafelki żyją na kartach listy i w drawerze; listy się przerysowują). Zapis: tylko lokalizacja
  // (PUT /api/catalog-v2/products/{id}/location — wystarcza zapis w Zamówieniach), potem wszystkie znaczki produktu.
  if (!w.__sbLocAddBound) {
    w.__sbLocAddBound = true;
    document.addEventListener('click', function(e: any) {
      var badge = e.target && e.target.closest && e.target.closest('.oc-sb-location-edit[data-loc-edit-product]');
      if (!badge) return;
      e.stopPropagation();  // kafelek / karta zamówienia nie może się przy tym otworzyć
      if (e.target.closest('input')) return;  // klik w samo pole edycji
      e.preventDefault();
      _edytujLokalizacjeProduktu(badge);
    }, true);
    document.addEventListener('keydown', function(e: any) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var badge = e.target && e.target.closest && e.target.closest('.oc-sb-location-edit[data-loc-edit-product]');
      if (!badge || e.target.closest('input')) return;
      e.preventDefault(); e.stopPropagation();
      _edytujLokalizacjeProduktu(badge);
    }, true);
  }
}


// ── Lokalizacja produktu na kafelku zamówienia (21.10.2026) ─────────────────────────────────────────────
var _PIN = '<i data-lucide="map-pin" style="width:12px;height:12px;flex-shrink:0"></i>';

var _PIN13 = '<i data-lucide="map-pin" style="width:13px;height:13px;flex-shrink:0"></i>';

/** Znaczek lokalizacji produktu na kafelku zamówienia / składniku zestawu: z lokalizacją albo „Dodaj lokalizację”.
 *  Klik (delegat w orders-shared) = wpisanie / zmiana w miejscu. Bez id produktu — sam tekst, bez edycji.
 *  `styl` 'zk' = wygląd kafelka LISTY zamówień (orders-list/cards.ts: amber za tytułem, krótkie „Dodaj”). */
export function lokalizacjaBadgeHtml(productId: any, location: any, styl?: string): string {
  var pid = productId != null && String(productId).trim() !== '' && String(productId) !== '0' ? String(productId) : '';
  var loc = String(location == null ? '' : location).trim();
  if (styl === 'zk') {
    if (!pid) return loc ? '<span class="zk-prod-loc zk-prod-loc--inline">' + _PIN13 + escapeHtml(loc) + '</span>' : '';
    var wspolneZk = ' role="button" tabindex="0" data-loc-edit-product="' + escapeAttr(pid) + '" data-loc-value="' + escapeAttr(loc) + '" data-loc-styl="zk" onclick="event.stopPropagation()"';
    return loc
      ? '<span class="zk-prod-loc zk-prod-loc--inline oc-sb-location-edit"' + wspolneZk + ' title="Kliknij, aby zmienić lokalizację">' + _PIN13 + escapeHtml(loc) + '</span>'
      : '<span class="zk-prod-loc zk-prod-loc--inline oc-sb-location-add oc-sb-location-edit" data-loc-add-product="' + escapeAttr(pid) + '"' + wspolneZk + ' title="Dodaj lokalizację magazynową" style="cursor:pointer;opacity:.6">' + _PIN13 + 'Dodaj</span>';
  }
  if (!pid) {
    return loc ? '<span class="oc-sb-location-badge">' + _PIN + '<span class="oc-sb-loc-txt">' + escapeHtml(loc) + '</span></span>' : '';
  }
  var wspolne = ' role="button" tabindex="0" data-loc-edit-product="' + escapeAttr(pid) + '" data-loc-value="' + escapeAttr(loc) + '" onclick="event.stopPropagation()"';
  return loc
    ? '<span class="oc-sb-location-badge oc-sb-location-edit"' + wspolne + ' title="Kliknij, aby zmienić lokalizację">' + _PIN + '<span class="oc-sb-loc-txt">' + escapeHtml(loc) + '</span></span>'
    : '<span class="oc-sb-location-badge oc-sb-location-add oc-sb-location-edit" data-loc-add-product="' + escapeAttr(pid) + '"' + wspolne + ' title="Dodaj lokalizację magazynową">' + _PIN + '<span class="oc-sb-loc-txt">Dodaj lokalizację</span></span>';
}

function _odswiezZnaczkiLokalizacji(pid: string, loc: string): void {
  document.querySelectorAll('.oc-sb-location-edit[data-loc-edit-product="' + CSS.escape(pid) + '"]').forEach(function(el) {
    var tmp = document.createElement('span');
    tmp.innerHTML = lokalizacjaBadgeHtml(pid, loc, el.getAttribute('data-loc-styl') || undefined);
    var nowy = tmp.firstElementChild;
    if (nowy && el.parentNode) el.parentNode.replaceChild(nowy, el);
  });
  try { (window as any).lucide && (window as any).lucide.createIcons(); } catch (_e) { /* ikony to dodatek */ }
}

function _edytujLokalizacjeProduktu(badge: HTMLElement): void {
  if (badge.classList.contains('is-editing')) return;
  var w = window as any;
  var pid = String(badge.getAttribute('data-loc-edit-product') || '');
  var stara = String(badge.getAttribute('data-loc-value') || '');
  if (!pid) return;
  badge.classList.add('is-editing');
  badge.style.opacity = '1';  // „Dodaj” na liście jest przygaszone — pole edycji ma być wyraźne
  badge.innerHTML = (badge.getAttribute('data-loc-styl') === 'zk' ? _PIN13 : _PIN) + '<input class="oc-sb-loc-input" maxlength="200" aria-label="Lokalizacja magazynowa" placeholder="np. G1,21" value="' + escapeAttr(stara) + '">';
  try { w.lucide && w.lucide.createIcons({ nodes: [badge] }); } catch (_e) { /* ikony to dodatek */ }
  var pole = badge.querySelector('input') as HTMLInputElement;
  pole.focus(); pole.select();
  var koniec = false;
  var przywroc = function(loc: string) { _odswiezZnaczkiLokalizacji(pid, loc); };
  var zapisz = async function() {
    if (koniec) return;
    koniec = true;
    var nowa = pole.value.replace(/\s+/g, ' ').trim();
    if (nowa === stara) { przywroc(stara); return; }
    pole.disabled = true;
    try {
      var r = await w.SellBaseAPI.request('PUT', '/api/catalog-v2/products/' + encodeURIComponent(pid) + '/location', { location: nowa });
      var zapisana = String((r && r.location != null) ? r.location : nowa);
      przywroc(zapisana);
      try { if (typeof w._invalidateOrdersPageCache === 'function') w._invalidateOrdersPageCache(); } catch (_e) { /* lista dociągnie */ }
      if (typeof w.showToast === 'function') w.showToast(zapisana ? 'Lokalizacja: ' + zapisana : 'Usunięto lokalizację', 'success');
    } catch (err: any) {
      przywroc(stara);
      var msg = String((err && err.message) || '');
      if (typeof w.showToast === 'function') {
        w.showToast(/403|uprawnie/i.test(msg) ? 'Brak uprawnień do zmiany lokalizacji' : 'Nie zapisano lokalizacji: ' + (msg || 'błąd'), 'error');
      }
    }
  };
  pole.addEventListener('keydown', function(ev) {
    ev.stopPropagation();  // skróty listy zamówień nie mogą łapać wpisywanych znaków
    if (ev.key === 'Enter') { ev.preventDefault(); void zapisz(); }
    else if (ev.key === 'Escape') { ev.preventDefault(); koniec = true; przywroc(stara); }
  });
  pole.addEventListener('blur', function() { void zapisz(); });
}
